// Static configuration for the clothing browser (/clothing).
// Product type groups mirror the `product_type` values in `product_styles`.

export interface TypeGroup {
  id: string;
  name: string;
  blurb: string;
  items: string[];
}

export const TYPE_GROUPS: TypeGroup[] = [
  {
    id: 'tops',
    name: 'Tops',
    blurb: 'Tees, polos, shirts and knits',
    items: ['T-Shirts', 'Polos', 'Shirts', 'Blouses', 'Sweatshirts', 'Hoodies', 'Fleece', 'Cardigans', 'Knitted Jumpers', 'Sports Overtops', 'Baselayers', 'Rugby Shirts', 'Tunics', 'Vests (t-shirt)'],
  },
  {
    id: 'outerwear',
    name: 'Outerwear',
    blurb: 'Jackets, gilets and softshells',
    items: ['Jackets', 'Gilets & Body Warmers', 'Softshells', 'Ponchos', 'Rain Suits', 'Waistcoats'],
  },
  {
    id: 'bottoms',
    name: 'Bottoms',
    blurb: 'Trousers, shorts and joggers',
    items: ['Shorts', 'Trousers', 'Chinos', 'Jeans', 'Skirts', 'Skorts', 'Sweatpants', 'Leggings', 'Trackwear', 'Dungarees', 'Dresses', 'Unitards'],
  },
  {
    id: 'workwear',
    name: 'Workwear',
    blurb: 'Aprons, hi-vis and chef wear',
    items: ['Aprons', 'Tabards', 'Bibs', 'Chef Jackets', 'Safety Vests', 'Coveralls', 'Kneepads', 'Helmets'],
  },
  {
    id: 'headwear',
    name: 'Headwear',
    blurb: 'Caps, beanies and hats',
    items: ['Caps', 'Beanies', 'Hats', 'Headbands', 'Snoods', 'Ear Muffs'],
  },
  {
    id: 'bags',
    name: 'Bags',
    blurb: 'Totes, backpacks and cases',
    items: ['Bags', 'Laptop Cases', 'Pencil Cases', 'Document Wallets', 'Wallets'],
  },
  {
    id: 'footwear',
    name: 'Footwear',
    blurb: 'Boots, trainers and socks',
    items: ['Boots', 'Shoes', 'Trainers', 'Slippers', 'Socks'],
  },
  {
    id: 'accessories',
    name: 'Accessories',
    blurb: 'Gloves, scarves, towels and more',
    items: ['Accessories', 'Belts', 'Braces', 'Ties', 'Scarves', 'Gloves', 'Umbrellas', 'Towels', 'Blankets', 'Soft Toys', 'Keyrings', 'Robes', 'Pyjamas', 'Onesies'],
  },
];

export const groupForType = (type: string): TypeGroup | undefined =>
  TYPE_GROUPS.find((g) => g.items.includes(type));

// Colour families come from the `color_shades` column ("Blue - Navy" → "Blue").
export interface ColourFamily {
  name: string;
  swatch: string; // CSS background
}

export const COLOUR_FAMILIES: ColourFamily[] = [
  { name: 'Black', swatch: '#111111' },
  { name: 'Grey', swatch: '#8a8f98' },
  { name: 'White', swatch: '#ffffff' },
  { name: 'Neutral', swatch: '#d9c9a8' },
  { name: 'Brown', swatch: '#7a4b23' },
  { name: 'Red', swatch: '#d0261b' },
  { name: 'Orange', swatch: '#f47a1f' },
  { name: 'Yellow', swatch: '#f5c518' },
  { name: 'Green', swatch: '#2f8f3b' },
  { name: 'Blue', swatch: '#1e56c8' },
  { name: 'Purple', swatch: '#7b3fbf' },
  { name: 'Pink', swatch: '#ec5fa0' },
  { name: 'Pattern', swatch: 'repeating-linear-gradient(135deg,#111 0 4px,#fff 4px 8px)' },
];

// Adult sizes ordered for display. Kids and trouser sizes are reached via
// the age-group filter instead of listing all 300+ size codes.
export const ADULT_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', 'One size'];

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'popular', label: 'Most popular' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'name-asc', label: 'Name A to Z' },
  { value: 'newest', label: 'Newest' },
];

export type SortKey = 'popular' | 'price-asc' | 'price-desc' | 'name-asc' | 'newest';

export const PRICE_PRESETS: { label: string; min?: number; max?: number }[] = [
  { label: 'Under £5', max: 5 },
  { label: '£5 – £10', min: 5, max: 10 },
  { label: '£10 – £20', min: 10, max: 20 },
  { label: '£20 – £40', min: 20, max: 40 },
  { label: '£40+', min: 40 },
];

export const PAGE_SIZE = 24;
